/**
 * Annual leave entitlement + the two dates the accrual runs from.
 *
 * Alshallal does not grant a flat yearly allowance: some staff hold 21 days,
 * some 30, some 90, and anything else has to be justified in writing. The
 * balance then accrues day by day from the date the employee came back from
 * their last annual leave (or their hire date, if they have never taken any),
 * capped at their own entitlement.
 *
 * Shared by the Create and Edit employee forms so both enforce the same shape.
 * Rendering is the caller's decision: only HR Manager, HR Admin and Super Admin
 * may see or change these fields.
 */
import React, { useEffect, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export const STANDARD_ANNUAL_LEAVE_DAYS = ['21', '30', '90'];
const NOT_SET = '_not_set_';
const OTHER = '_other_';

export const AnnualLeaveFields = ({ values = {}, onChange, errors = {}, isRTL = false }) => {
  const days = values.annual_leave_days ?? '';
  const isStandard = STANDARD_ANNUAL_LEAVE_DAYS.includes(String(days));
  const [customChosen, setCustomChosen] = useState(days !== '' && !isStandard);

  // The employee record arrives after mount on the Edit form, so a custom
  // entitlement already on file has to switch the form into custom mode.
  useEffect(() => {
    if (days !== '' && !STANDARD_ANNUAL_LEAVE_DAYS.includes(String(days))) {
      setCustomChosen(true);
    }
  }, [days]);

  const selectValue = customChosen ? OTHER : (isStandard ? String(days) : NOT_SET);

  const handleSelect = (value) => {
    if (value === OTHER) {
      setCustomChosen(true);
      if (isStandard) onChange('annual_leave_days', '');
      return;
    }
    setCustomChosen(false);
    onChange('annual_leave_days_justification', '');
    onChange('annual_leave_days', value === NOT_SET ? '' : value);
    onChange('annual_leave_cycle_years', value === '90' ? '3' : '1');
  };

  return (
    <div
      className="p-4 border border-emerald-200 bg-emerald-50 rounded-lg space-y-4"
      data-testid="annual-leave-fields"
    >
      <div>
        <h4 className="font-medium text-emerald-900 flex items-center gap-2">
          <CalendarDays className="h-4 w-4" />
          {isRTL ? 'استحقاق الإجازة السنوية' : 'Annual Leave Entitlement'}
        </h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className={cn(errors.annual_leave_days && 'text-red-500')}>
            {isRTL ? 'أيام الإجازة السنوية' : 'Annual Leave Days'}
          </Label>
          <Select value={selectValue} onValueChange={handleSelect}>
            <SelectTrigger data-testid="select-annual-leave-days">
              <SelectValue placeholder={isRTL ? 'اختر الاستحقاق' : 'Select entitlement'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NOT_SET}>
                {isRTL ? '— غير محدد —' : '— Not set —'}
              </SelectItem>
              {STANDARD_ANNUAL_LEAVE_DAYS.map(option => (
                <SelectItem key={option} value={option} data-testid={`annual-preset-${option}`}>
                  {isRTL ? `${option} يوماً / ${option === '90' ? 3 : 1} سنة` : `${option} days / ${option === '90' ? '3 years' : '1 year'}`}
                </SelectItem>
              ))}
              <SelectItem value={OTHER}>{isRTL ? 'أخرى' : 'Other'}</SelectItem>
            </SelectContent>
          </Select>
          {selectValue === NOT_SET && (
            <p className="text-xs text-emerald-800/80">
              {isRTL
                ? 'غير محدد: يستخدم النظام الاستحقاق الافتراضي لنوع الإجازة (21 يوماً).'
                : 'Not set: the system falls back to the leave type default (21 days).'}
            </p>
          )}
        </div>

        {customChosen && (
          <div className="space-y-2">
            <Label className={cn(errors.annual_leave_days && 'text-red-500')}>
              {isRTL ? 'عدد الأيام (أخرى)' : 'Number of days (Other)'}
              <span className="text-red-500 ms-1">*</span>
            </Label>
            <Input
              type="number"
              min="1"
              max="365"
              value={days}
              onChange={(e) => onChange('annual_leave_days', e.target.value)}
              className={cn(errors.annual_leave_days && 'border-red-500')}
              placeholder={isRTL ? 'مثال: 45' : 'e.g. 45'}
              data-testid="input-annual-leave-days-custom"
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="annual-cycle-years">{isRTL ? 'مدة الدورة (سنوات)' : 'Cycle length (years)'}</Label>
          <Input id="annual-cycle-years" type="number" min="1" max="100" step="1"
            data-testid="annual-cycle-years" value={values.annual_leave_cycle_years || '1'}
            onChange={e => onChange('annual_leave_cycle_years', e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="annual-change-reason">{isRTL ? 'سبب التغيير' : 'Written reason for this change'}</Label>
          <Textarea id="annual-change-reason" data-testid="annual-change-reason" minLength={10} maxLength={1000}
            value={values.annual_leave_change_reason || ''} onChange={e => onChange('annual_leave_change_reason', e.target.value)} />
        </div>
      </div>

      {errors.annual_leave_days && (
        <p className="text-sm text-red-500" data-testid="error-annual-leave-days">
          {errors.annual_leave_days}
        </p>
      )}

      {(customChosen || (days && ![['21','1'],['30','1'],['90','3']].some(([d,y]) => d === String(days) && y === String(values.annual_leave_cycle_years || '1')))) && (
        <div className="space-y-2">
          <Label className={cn(errors.annual_leave_days_justification && 'text-red-500')}>
            {isRTL ? 'مبرر الاستحقاق غير القياسي' : 'Justification for a non-standard entitlement'}
            <span className="text-red-500 ms-1">*</span>
          </Label>
          <Textarea
            rows={2}
            value={values.annual_leave_days_justification || ''}
            onChange={(e) => onChange('annual_leave_days_justification', e.target.value)}
            className={cn(errors.annual_leave_days_justification && 'border-red-500')}
            placeholder={isRTL
              ? 'سبب منح استحقاق مختلف عن 21 / 30 / 90 يوماً (10 أحرف على الأقل)'
              : 'Why this employee holds something other than 21 / 30 / 90 days (at least 10 characters)'}
            data-testid="input-annual-leave-justification"
          />
          {errors.annual_leave_days_justification && (
            <p className="text-sm text-red-500" data-testid="error-annual-leave-justification">
              {errors.annual_leave_days_justification}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>{isRTL ? 'بداية آخر إجازة سنوية' : 'Last Annual Leave Start Date'}</Label>
          <Input
            type="date"
            value={values.last_annual_leave_start_date || ''}
            onChange={(e) => onChange('last_annual_leave_start_date', e.target.value)}
            data-testid="input-last-annual-leave-start-date"
          />
          <p className="text-xs text-emerald-800/80">
            {isRTL
              ? 'يُعبأ تلقائياً عند موافقة الموارد البشرية على إجازة سنوية، ويمكن تعديله.'
              : 'Actual departure date.'}
          </p>
        </div>

        <div className="space-y-2">
          <Label className={cn(errors.last_annual_leave_rejoin_date && 'text-red-500')}>
            {isRTL ? 'تاريخ المباشرة بعد الإجازة' : 'Rejoin Date'}
          </Label>
          <Input
            type="date"
            value={values.last_annual_leave_rejoin_date || ''}
            onChange={(e) => onChange('last_annual_leave_rejoin_date', e.target.value)}
            className={cn(errors.last_annual_leave_rejoin_date && 'border-red-500')}
            data-testid="input-last-annual-leave-rejoin-date"
          />
          {errors.last_annual_leave_rejoin_date ? (
            <p className="text-sm text-red-500" data-testid="error-annual-leave-rejoin-date">
              {errors.last_annual_leave_rejoin_date}
            </p>
          ) : (
            <p className="text-xs text-emerald-800/80">
              {isRTL
                ? 'من هذا التاريخ يبدأ احتساب الرصيد من جديد.'
                : 'Actual confirmed return date.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnualLeaveFields;
